import {
  ReactElement,
  forwardRef,
  ComponentPropsWithoutRef,
  useId,
  useState,
  useRef,
} from "react";
import cx from "classnames";
import { Label } from "../Label";
import { Spinner } from "../Spinner";
import textFieldStyles from "../TextField/TextField.module.scss";
import numberFieldStyles from "./NumberField.module.scss";
import { Helper, HelperProps } from "../../internal/components";
import { LayoutUtilProps, Size, FieldLabelProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import { Button } from "../Button";
import { Icon } from "../Icon";
import IconAdd from "@servicetitan/hammer-icon/mdi/round/add.svg";
import IconRemove from "@servicetitan/hammer-icon/mdi/round/remove.svg";
import { useFocusWithin } from "../../hooks/useFocusWithin";
import { useNumberField } from "../../internal/hooks";

export type NumberFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  | "size"
  | "prefix"
  | "type"
  | "inputMode"
  | "value"
  | "defaultValue"
  | "onFocus"
  | "onBlur"
  | "onChange"
> &
  LayoutUtilProps &
  FieldLabelProps & {
    error?: ReactElement | string | boolean;
    label?: string;
    prefix?: string | ReactElement;
    suffix?: string | ReactElement;
    size?: Extract<Size, "small" | "medium" | "large">;
    description?: HelperProps["description"];
    errorAriaLive?: HelperProps["errorAriaLive"];
    hint?: HelperProps["hint"];
    loading?: boolean;
    disabled?: boolean;
    minValue?: number;
    maxValue?: number;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    step?: number;
    value?: number | null;
    defaultValue?: number | null;
    onChange?: (value: number | null) => void;
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (event: FocusEvent) => void;
    hideControls?: boolean;
  };

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      error,
      errorAriaLive: errorAriaLiveProp = "assertive",
      defaultValue: defaultValueProp,
      value: valueProp,
      label,
      moreInfo,
      prefix,
      suffix,
      size,
      required,
      onChange: onChangeProp,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      id: idProp,
      description,
      hint,
      loading,
      disabled,
      readOnly,
      labelProps,
      style,
      placeholder,
      minValue,
      maxValue,
      maximumFractionDigits = 0,
      minimumFractionDigits = 0,
      step = 1,
      hideControls = false,
      ...rest
    } = componentProps;
    const numberFieldRef = useRef<HTMLDivElement>(null);

    const [openInfo, setOpenInfo] = useState(false);
    const [errorAriaLive, setErrorAriaLive] =
      useState<HelperProps["errorAriaLive"]>("off");

    const { ref: wrapperRef } = useFocusWithin({
      onFocus: (e) => {
        setOpenInfo(true);
        setErrorAriaLive(errorAriaLiveProp);
        onFocusProp?.(e);
      },
      onBlur: (e) => {
        setOpenInfo(false);
        setErrorAriaLive("off");
        onBlurProp?.(e);
      },
    });

    const { inputProps, inputRef, incrementButtonProps, decrementButtonProps } =
      useNumberField({
        minValue,
        maxValue,
        maximumFractionDigits,
        minimumFractionDigits,
        step,
        value: valueProp,
        defaultValue: defaultValueProp,
        onChange: onChangeProp,
        label,
      });

    const combinedInputRef = useMergeRefs([numberFieldRef, inputRef, ref]);

    const labelUid = useId();
    const id = idProp ?? labelUid;
    const prefixUid = useId();
    const suffixUid = useId();
    const helperUid = useId();

    const errorMessage = typeof error !== "boolean" ? error : undefined;

    const styleCombined = { ...style, ...layoutStyles };

    return (
      <div
        className={cx(textFieldStyles["textfield"], className)}
        data-anv="number-field"
        style={styleCombined}
        ref={wrapperRef}
      >
        {label && (
          <Label
            moreInfo={moreInfo}
            openMoreInfo={openInfo}
            required={required}
            htmlFor={id}
            {...labelProps}
          >
            {label}
          </Label>
        )}
        <div
          className={cx(textFieldStyles["input-wrapper"], {
            [textFieldStyles["small"]]: size === "small",
            [textFieldStyles["large"]]: size === "large",
            [numberFieldStyles["small"]]: size === "small",
            [numberFieldStyles["large"]]: size === "large",
          })}
          role="presentation"
          onClick={() => numberFieldRef.current?.focus()}
        >
          {prefix ? (
            <div
              aria-hidden
              className={textFieldStyles["prefix"]}
              id={`prefix${prefixUid}`}
            >
              {prefix}
            </div>
          ) : null}
          <input
            id={id}
            className={cx(textFieldStyles["input"], {
              [textFieldStyles["error"]]: error,
            })}
            {...rest}
            {...inputProps}
            required={required}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            ref={combinedInputRef}
            aria-label={label ? undefined : placeholder}
            aria-describedby={helperUid}
            aria-invalid={error ? !!error : undefined}
          />
          {loading ? (
            <div
              className={textFieldStyles["loading-spinner-wrapper"]}
              data-testid="spinner"
            >
              <Spinner />
            </div>
          ) : null}
          {suffix ? (
            <div
              className={textFieldStyles["suffix"]}
              aria-hidden
              id={`suffix${suffixUid}`}
            >
              {suffix}
            </div>
          ) : null}
          {!hideControls && !disabled && !readOnly && (
            <div className={numberFieldStyles["stepper-buttons"]}>
              <Button
                appearance="secondary"
                size="small"
                {...decrementButtonProps}
              >
                <Icon svg={IconRemove} />
              </Button>
              <Button
                appearance="secondary"
                size="small"
                {...incrementButtonProps}
              >
                <Icon svg={IconAdd} />
              </Button>
            </div>
          )}
        </div>
        {hint || errorMessage || description ? (
          <Helper
            id={helperUid}
            hint={hint}
            showCounter={false}
            errorMessage={errorMessage}
            errorAriaLive={errorAriaLive}
            description={description}
          />
        ) : null}
      </div>
    );
  },
);

NumberField.displayName = "NumberField";
